import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getGeneratedCode } from '../../common/modules/widgetCode';
import { FormFields, Tariff, ValueOf } from '../../common/types';
import { getAppCodeFromUrl, getSystemLanguage } from '../../common/utils';

export interface FormSliceState {
    fields: FormFields;
    isCodeShown: boolean;
    generatedCode: string;
    availableTariffs: Tariff[];
    generatedUrl: string;
    generatedQRImage: string;
    shortUrl: {
        status: 'idle' | 'loading' | 'succeeded' | 'failed';
        result: string;
    };
}

const initialState: FormSliceState = {
    fields: {
        address: '',
        addressShort: '',
        validAddress: '',
        addressCoords: {
            lat: '55.733842',
            lon: '37.588144',
        },
        type: 'badge',
        routeType: 'to',
        title: '',
        titleFixed: 'Вызвать такси',
        clid: '',
        image: '',
        imageName: '',
        isPriceShown: false,
        tariff: 'econom',
        style: 'normal',
        subtitle: 'address',
        priceClid: '',
        priceKey: '',
        app: getAppCodeFromUrl(),
        lang: getSystemLanguage(),
        erid: '',
    },
    availableTariffs: ['econom'],
    isCodeShown: false,
    generatedCode: '',
    generatedUrl: '',
    generatedQRImage: '',
    shortUrl: {
        status: 'loading',
        result: '',
    },
};

export const formSlice = createSlice({
    name: 'form',
    initialState,
    reducers: {
        setField(
            state: FormSliceState,
            action: PayloadAction<{ name: keyof FormFields; value: ValueOf<FormFields> }>
        ): FormSliceState {
            const { name, value } = action.payload;
            if (typeof state.fields[name] !== 'undefined') {
                return { ...state, fields: { ...state.fields, [name]: value } };
            }
            return state;
        },
        setGeneratedCode: (state, { payload }) => {
            state.generatedCode = payload;
        },
        setGeneratedUrl: (state, { payload }) => {
            state.generatedUrl = payload;
        },
        generateCode: (state) => {
            const { widget, url } = getGeneratedCode(state.fields);
            state.generatedCode = widget;
            state.generatedUrl = url;
        },
        setCode: (state, { payload: code }) => {
            state.generatedCode = code;
        },
        setQRImage: (state, action: PayloadAction<string>) => {
            const qrImage = action.payload;
            state.generatedQRImage = qrImage;
        },
        hideCode: (state) => {
            state.isCodeShown = false;
        },
        showCode: (state) => {
            state.isCodeShown = true;
        },
        setAvailableTariffs: (state, { payload: tariffs }) => {
            state.availableTariffs = tariffs;
            state.fields.tariff = tariffs.includes(state.fields.tariff)
                ? state.fields.tariff
                : tariffs[0];
        },
        setShortUrlLoading: (state) => {
            state.shortUrl.status = 'loading';
        },
        setShortUrlFailed: (state) => {
            state.shortUrl.status = 'failed';
        },
        setShortUrl: (state, action) => {
            const { payload } = action;
            state.shortUrl.result = payload;
            state.shortUrl.status = 'succeeded';
        },
    },
});

export const {
    setField,
    hideCode,
    showCode,
    setGeneratedCode,
    setGeneratedUrl,
    generateCode,
    setAvailableTariffs,
    setQRImage,
    setShortUrl,
    setShortUrlLoading,
    setShortUrlFailed,
    setCode,
} = formSlice.actions;
